import { z } from 'zod';

/** Стоимость публикации услуги на модерацию. */
export const SERVICE_PUBLISH_COST = 20;

/** Стоимость отправки события на модерацию. */
export const EVENT_PUBLISH_COST = 10;

/**
 * Сколько событий обычный юзер может СОЗДАТЬ за сутки.
 *
 * Считаются только реально созданные события (`Event.createdAt` за последние
 * 24 часа), а не попытки: до 29.09.2026 лимит держал in-memory `@Throttle` на
 * `POST /events`, и три отказа валидации подряд запирали человека на сутки —
 * ровно это случилось на демо для бизнеса. Заодно лимит переживает рестарт
 * api, а раньше обнулялся вместе с процессом.
 */
export const EVENT_CREATE_DAILY_LIMIT = 3;

/** Бесплатных запросов к ИИ-ассистенту в сутки (UTC), далее — DAILY_QUOTA_OVERAGE_COST. */
export const ASSISTANT_DAILY_FREE_REQUESTS = 3;

/** Бесплатных игровых действий (отчёт / смена миссии) в сутки (UTC), далее — DAILY_QUOTA_OVERAGE_COST. */
export const GAME_DAILY_FREE_REQUESTS = 3;

/** Стоимость запроса сверх дневной квоты (ассистент и игра). */
export const DAILY_QUOTA_OVERAGE_COST = 1;

// ── Начисления, которые показывает экран «как заработать энергию» ───────────
// Живут в контрактах, а не только в `coins.constants.ts`, потому что суммы
// одновременно начисляет бэк и обещает mini-app. Пока это были два независимых
// литерала, урезание тарифа отметки (2026-08-13) разъехалось молча: экран
// продолжал обещать 50⚡ там, где начислялось 5⚡.

/** Реферальный бонус: приглашённый впервые открыл приложение. */
export const REFERRAL_BONUS_AMOUNT = 10;

/** Веха `ref-photo:<invitee>`: приглашённый впервые отметился на групповом фото. */
export const REFERRAL_PHOTO_BONUS_AMOUNT = 5;

/** Веха `ref-invite:<invitee>`: приглашённый сам впервые кого-то привёл. */
export const REFERRAL_INVITE_BONUS_AMOUNT = 10;

/** Бонус отмеченному на групповом фото (`phototag:<userId>`, один раз за всё время). */
export const PHOTO_TAG_TAGGED_BONUS = 5;

/** Бонус организатору за каждого отмеченного (`phototag-org:<organizerId>:<taggedUserId>`, один раз на человека). */
export const PHOTO_TAG_ORGANIZER_BONUS = 10;

// Приглашение организатора (`reforg-<refcode>`) — обе стороны. Здесь, а не только
// в `coins.constants.ts`, потому что суммы обещает текст приглашения в mini-app
// (модалка внешнего организатора, хвост заявки): пока это были независимые
// литералы, копирайт мог пережить урезание тарифа и обещать несуществующее.

/** Пригласившему организатора (`orginvite:<inviteeId>`). Урезано 100 → 10 в 2026-08-15. */
export const ORGANIZER_INVITE_BONUS_AMOUNT = 10;

/** Приглашённому организатору (`orginvite-welcome:<userId>`). Урезано 100 → 10 в 2026-08-15. */
export const ORGANIZER_WELCOME_BONUS_AMOUNT = 10;

// ── Игра «Лило»: партия покупается целиком ──────────────────────────────────
// Здесь, а не только в `coins.constants.ts`: цену показывает платёжная стена в
// mini-app, и независимый литерал разъехался бы с тарифом молча.

/** Стоимость полной партии (`lilo-session:<sessionId>`). Оплачивается один раз, дальше броски и реплики внутри партии бесплатны. */
export const LILO_SESSION_COST = 30;

/** Бесплатных бросков на пользователя за всё время. Меньше минимальной длины партии (4 хода) — закончить игру бесплатно нельзя. */
export const LILO_TRIAL_ROLLS = 3;

/** Потолок реплик игрока в одной клетке. Ограничивает LLM-расход внутри оплаченной партии и в тизере. */
export const LILO_MESSAGES_PER_TURN_LIMIT = 10;

export const DailyEnergyQuotaDto = z.object({
  freePerDay: z.number().int().min(0),
  usedToday: z.number().int().min(0),
  freeRemaining: z.number().int().min(0),
  nextCost: z.number().int().min(0),
});
export type DailyEnergyQuotaDto = z.infer<typeof DailyEnergyQuotaDto>;

/** @param usedToday — сколько запросов уже сделано в текущих сутках (UTC). */
export function computeDailyEnergyQuota(
  freePerDay: number,
  usedToday: number,
  overageCost = DAILY_QUOTA_OVERAGE_COST,
): DailyEnergyQuotaDto {
  const freeRemaining = Math.max(0, freePerDay - usedToday);
  return {
    freePerDay,
    usedToday,
    freeRemaining,
    nextCost: freeRemaining > 0 ? 0 : overageCost,
  };
}

/** Ключ календарного дня для дневных квот (граница 00:00 UTC). */
export function utcEnergyDayKey(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}
